import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { content as staticContent } from "@/lib/content";
import { deepMerge } from "@/lib/deepMerge";
import { normalizeConfig, DEFAULT_IMAGES } from "@/lib/siteDefaults";
import {
  Save,
  UploadCloud,
  Eye,
  ExternalLink,
  LogOut,
  RotateCcw,
  Loader2,
} from "lucide-react";
import ThemeEditor from "@/pages/admin/ThemeEditor";
import PageBuilder from "@/pages/admin/PageBuilder";
import JournalManager from "@/pages/admin/JournalManager";
import PageEditor from "@/pages/admin/PageEditor";
import { MediaManager, CourseManager, ProjectManager, SeoManager } from "@/pages/admin/contentManagers";
import { AdminSaveContext } from "@/lib/adminSaveContext";

function buildDraft(raw) {
  const norm = normalizeConfig(raw);
  return {
    theme: norm.theme,
    sections: norm.sections,
    content: {
      ro: deepMerge(staticContent.ro, raw?.content?.ro || {}),
      en: deepMerge(staticContent.en, raw?.content?.en || {}),
    },
    images: { ...DEFAULT_IMAGES, ...(raw?.images || {}) },
  };
}

function TabBtn({ id, active, onClick, children }) {
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={`admin-tab-${id}`}
      className={`eyebrow px-5 py-2.5 border transition-colors ${
        active ? "bg-ink text-paper border-ink" : "border-ink/25 hover:border-ink"
      }`}
    >
      {children}
    </button>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState("pages");
  const [lang, setLang] = useState("ro");
  const [showPreview, setShowPreview] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const iframeRef = useRef(null);
  const draftRef = useRef(null);
  draftRef.current = draft;
  const entitySaverRef = useRef(null);
  const registerSaver = useCallback((fn) => {
    entitySaverRef.current = fn;
  }, []);

  const loadDraft = useCallback(async () => {
    const { data } = await api.get("/site/draft");
    setDraft(buildDraft(data));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("om_token");
    if (!token) {
      nav("/admin/login");
      return;
    }
    api
      .get("/auth/me")
      .then(async () => {
        await loadDraft();
        setReady(true);
      })
      .catch(() => {
        localStorage.removeItem("om_token");
        nav("/admin/login");
      });
  }, [nav, loadDraft]);

  const postPreview = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: "om-preview", config: draftRef.current },
      "*"
    );
  }, []);

  // iframe announces readiness → push current draft
  useEffect(() => {
    const onMsg = (e) => {
      if (e.data && e.data.type === "om-preview-ready") postPreview();
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [postPreview]);

  // live push on every change (debounced)
  useEffect(() => {
    if (!showPreview || !draft) return;
    const id = setTimeout(postPreview, 180);
    return () => clearTimeout(id);
  }, [draft, showPreview, postPreview]);

  const saveDraft = async (silent = false) => {
    setSaving(true);
    try {
      if (entitySaverRef.current) await entitySaverRef.current();
      await api.put("/site/draft", draft);
      if (!silent) toast.success("Salvat");
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Salvare eșuată");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      if (entitySaverRef.current) await entitySaverRef.current();
      await api.put("/site/draft", draft);
      await api.post("/site/publish");
      toast.success("Publicat! Site-ul public este actualizat.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Publicare eșuată");
    } finally {
      setPublishing(false);
    }
  };

  const openPreviewTab = async () => {
    const ok = await saveDraft(true);
    if (ok) window.open(`${window.location.origin}/?ompreview=draft`, "_blank");
  };

  const reset = async () => {
    if (!window.confirm("Resetezi draftul la valorile implicite?")) return;
    await api.post("/site/reset");
    await loadDraft();
    toast.success("Draft resetat");
  };

  const logout = () => {
    localStorage.removeItem("om_token");
    nav("/admin/login");
  };

  if (!ready || !draft)
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center eyebrow">
        <Loader2 className="animate-spin mr-2" size={16} /> Se încarcă…
      </div>
    );

  const previewable = ["pages", "theme", "about"].includes(tab);

  return (
    <AdminSaveContext.Provider value={{ registerSaver }}>
    <div className="min-h-screen bg-paper text-ink">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-paper/95 backdrop-blur border-b border-ink px-5 md:px-8 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-display text-2xl leading-none">
              Oana <span className="italic">Mudura</span>
            </div>
            <div className="eyebrow mt-1 text-ink/55">CMS · Editorial Engine</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setShowPreview((v) => !v)}
              data-testid="admin-toggle-preview"
              className={`eyebrow inline-flex items-center gap-2 border px-4 py-2.5 transition-colors ${
                showPreview ? "bg-ink text-paper border-ink" : "border-ink/25 hover:border-ink"
              }`}
            >
              <Eye size={14} /> Preview
            </button>
            <button
              onClick={openPreviewTab}
              data-testid="admin-preview-tab"
              className="eyebrow inline-flex items-center gap-2 border border-ink/25 px-4 py-2.5 hover:border-ink transition-colors"
            >
              <ExternalLink size={14} /> Tab nou
            </button>
            <button
              onClick={() => saveDraft()}
              disabled={saving}
              data-testid="admin-save-draft"
              className="eyebrow inline-flex items-center gap-2 border border-ink px-4 py-2.5 hover:bg-ink hover:text-paper transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvează
            </button>
            <button
              onClick={publish}
              disabled={publishing}
              data-testid="admin-publish"
              className="eyebrow inline-flex items-center gap-2 bg-ink text-paper px-5 py-2.5 hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {publishing ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Publică
            </button>
            <button onClick={reset} title="Resetează" data-testid="admin-reset" className="eyebrow p-2.5 border border-ink/25 hover:border-ink transition-colors">
              <RotateCcw size={14} />
            </button>
            <button onClick={logout} data-testid="admin-logout" title="Deconectare" className="eyebrow p-2.5 border border-ink/25 hover:border-ink transition-colors">
              <LogOut size={14} />
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <TabBtn id="pages" active={tab === "pages"} onClick={setTab}>Home</TabBtn>
          <TabBtn id="services" active={tab === "services"} onClick={setTab}>Servicii</TabBtn>
          <TabBtn id="about" active={tab === "about"} onClick={setTab}>Despre</TabBtn>
          <TabBtn id="media" active={tab === "media"} onClick={setTab}>Media</TabBtn>
          <TabBtn id="courses" active={tab === "courses"} onClick={setTab}>Library</TabBtn>
          <TabBtn id="projects" active={tab === "projects"} onClick={setTab}>Portofoliu</TabBtn>
          <TabBtn id="journal" active={tab === "journal"} onClick={setTab}>Jurnal</TabBtn>
          <TabBtn id="seo" active={tab === "seo"} onClick={setTab}>SEO</TabBtn>
          <TabBtn id="theme" active={tab === "theme"} onClick={setTab}>Temă</TabBtn>
        </div>
      </div>

      {/* Body */}
      <div className="flex">
        <div className={`${showPreview && previewable ? "w-full lg:w-1/2" : "w-full"} px-5 md:px-8 py-8`}>
          {tab === "theme" && (
            <ThemeEditor theme={draft.theme} onChange={(theme) => setDraft((d) => ({ ...d, theme }))} />
          )}
          {tab === "pages" && (
            <PageBuilder draft={draft} setDraft={setDraft} lang={lang} setLang={setLang} />
          )}
          {tab === "about" && (
            <PageEditor
              draft={draft}
              setDraft={setDraft}
              lang={lang}
              setLang={setLang}
              sectionKey="about"
              title="Despre"
              images={[
                { key: "about_portrait", label: "Portret principal" },
                { key: "about_secondary", label: "Portret secundar" },
              ]}
              imageLists={[
                { key: "about_gallery", label: "Galerie orizontală (oricâte foto)" },
              ]}
            />
          )}
          {tab === "services" && (
            <PageEditor draft={draft} setDraft={setDraft} lang={lang} setLang={setLang} sectionKey="services" title="Servicii" />
          )}
          {tab === "media" && <MediaManager lang={lang} setLang={setLang} />}
          {tab === "courses" && <CourseManager lang={lang} draft={draft} setDraft={setDraft} setLang={setLang} />}
          {tab === "projects" && <ProjectManager lang={lang} setLang={setLang} />}
          {tab === "journal" && <JournalManager />}
          {tab === "seo" && (
            <SeoManager draft={draft} setDraft={setDraft} lang={lang} setLang={setLang} />
          )}
        </div>

        {showPreview && previewable && (
          <div className="hidden lg:block w-1/2 border-l border-ink/20">
            <div className="sticky top-[137px] h-[calc(100vh-137px)]">
              <div className="eyebrow text-ink/40 px-4 py-2 border-b border-ink/10 flex items-center justify-between">
                <span>Previzualizare live</span>
                <span>{draft.theme.spacing?.section}</span>
              </div>
              <iframe
                ref={iframeRef}
                title="Previzualizare"
                data-testid="preview-iframe"
                src={`${window.location.origin}/?ompreview=live`}
                className="w-full h-[calc(100%-33px)] bg-paper"
              />
            </div>
          </div>
        )}
      </div>
    </div>
    </AdminSaveContext.Provider>
  );
}
